import {
	type AnyQuestion,
	isSome,
	type ProductAmlQuestionSet,
	parseFormValues,
	type QuestionnaireFormValues,
} from "@sveabank/cc-common";
import { useApplicationDetails } from "../../hooks/application";
import { useApplicationId } from "../../hooks/useApplicationId";
import { useCountries } from "../../hooks/useCountries";
import { useSteps } from "../../hooks/useSteps";
import {
	useAssignSniCodesMutation,
	useConfirmBeneficialOwnersMutation,
	useFetchAmlQuestionsQuery,
	useFetchBeneficialOwnersQuery,
	useFetchQuestionsQuery,
	useFetchSniCodesQuery,
	usePostAmlQuestionsMutation,
	usePostApplicationQuestionsMutation,
	usePostBeneficialOwnerQuestionsMutation,
} from "../../services/kyc";
import { useFetchSelectedPackageQuery } from "../../services/package";
import { stepIs } from "../../utils/application";
import { getApplicationQuestionIds, hasCompletedAmlQuestionsForProduct } from "./utils";

export const useKyc = () => {
	const applicationId = useApplicationId();

	const { steps = [], isLoading: isLoadingSteps, areStepsCompleted, someStepExists } = useSteps();
	const { isOrgEf, applicationDetails, isApplicationDetailsLoading } = useApplicationDetails();

	const boQuestionStepState = steps?.find(stepIs("BeneficialOwnerQuestionsStepV1"))?.state || [];

	const { data: amlQuestions, isLoading: isLoadingAmlQuestions } =
		useFetchAmlQuestionsQuery(applicationId);

	const { data: applicationQuestions, isLoading: isLoadingApplicationQuestions } =
		useFetchQuestionsQuery(
			{
				applicationId,
				questionIds: getApplicationQuestionIds(steps),
			},
			{ skip: !(steps?.length > 0) },
		);

	const { allCountries, isLoading: isLoadingCountries } = useCountries();

	const { data: { products } = {}, isLoading: isLoadingPackage } =
		useFetchSelectedPackageQuery(applicationId);

	const { data: beneficialOwnership, isLoading: isLoadingBeneficialOwners } =
		useFetchBeneficialOwnersQuery(applicationId);

	const [postApplicationQuestions] = usePostApplicationQuestionsMutation();
	const [postBeneficialOwnerQuestions] = usePostBeneficialOwnerQuestionsMutation();
	const [postAmlQuestions] = usePostAmlQuestionsMutation();
	const [confirmBeneficialOwnersMutation, { isLoading: isSubmittingBeneficialOwner }] =
		useConfirmBeneficialOwnersMutation({
			fixedCacheKey: "confirmBeneficialOwners",
		});
	const [assignSniCodesMutation, { isLoading: isSubmittingSniCodes }] = useAssignSniCodesMutation();

	const submitApplicationQuestions =
		(questions: Array<AnyQuestion>) => async (values: QuestionnaireFormValues) => {
			const postValues = parseFormValues(questions, values);
			await postApplicationQuestions({
				applicationId,
				questions: postValues,
			}).unwrap();
		};

	const confirmBeneficialOwners = async (isConfirmed: boolean) => {
		await confirmBeneficialOwnersMutation({
			applicationId,
			beneficialOwnersConfirmed: isConfirmed,
		}).unwrap();
	};

	const submitBeneficialOwnerQuestions = async (
		questions: Array<AnyQuestion>,
		values: QuestionnaireFormValues,
		beneficialOwnerQuestionKey: string,
	) => {
		const postValues = parseFormValues(questions, values);
		await postBeneficialOwnerQuestions({
			applicationId,
			questions: postValues,
			beneficialOwnerQuestionKey,
		}).unwrap();
	};

	const submitAmlQuestions =
		({ questions, productId }: ProductAmlQuestionSet) =>
		async (values: QuestionnaireFormValues) => {
			const postValues = parseFormValues(questions, values);
			await postAmlQuestions({
				applicationId,
				questions: postValues,
				productId,
			}).unwrap();
		};

	const assignSniCodes = async (sniCodes: Array<string>) => {
		await assignSniCodesMutation({ applicationId, sniCodes }).unwrap();
	};

	const { data: allSniCodes, isLoading: isLoadingSniCodes } = useFetchSniCodesQuery();

	const hasCompletedBeneficialOwner = areStepsCompleted("ConfirmBeneficialOwnershipStep");

	const hasCompletedBoQuestionsStep = areStepsCompleted("BeneficialOwnerQuestionsStepV1");

	const hasCompletedApplicationQuestions = areStepsCompleted("ApplicationQuestionsStep");

	const shouldDisplayBeneficialOwner = someStepExists("ConfirmBeneficialOwnershipStep");

	const shouldDisplayBeneficialOwnerQuestions =
		isOrgEf || beneficialOwnership?.beneficialOwnershipStatus === "beneficialOwnersExists";

	const shouldDisplaySniCode = someStepExists("AssignSniCodesStep");

	const hasCompletedSniCode = areStepsCompleted("AssignSniCodesStep");

	const kycCompleted =
		hasCompletedBeneficialOwner &&
		hasCompletedBoQuestionsStep &&
		hasCompletedApplicationQuestions &&
		amlQuestions
			? amlQuestions.every((q) => hasCompletedAmlQuestionsForProduct(steps, q.productId))
			: true;

	const isLoading = isSome(
		isLoadingAmlQuestions,
		isLoadingApplicationQuestions,
		isLoadingPackage,
		isLoadingSteps,
		isLoadingBeneficialOwners,
		isLoadingCountries,
		isApplicationDetailsLoading,
		isLoadingSniCodes,
	);

	return {
		amlQuestions,
		applicationQuestions,
		products,
		steps,
		submitAmlQuestions,
		submitApplicationQuestions,
		beneficialOwnership,
		confirmBeneficialOwners,
		hasCompletedBeneficialOwner,
		hasCompletedBoQuestionsStep,
		isSubmittingBeneficialOwner,
		submitBeneficialOwnerQuestions,
		boQuestionStepState,
		hasCompletedApplicationQuestions,
		allCountries,
		shouldDisplayBeneficialOwner,
		applicationDetails,
		isOrgEf,
		allSniCodes,
		shouldDisplaySniCode,
		hasCompletedSniCode,
		assignSniCodes,
		isSubmittingSniCodes,
		isLoading,
		kycCompleted,
		shouldDisplayBeneficialOwnerQuestions,
	};
};
